import { useNavigate } from "react-router-dom";
import {
  Settings as SettingsIcon,
  FolderOpen,
  Bookmark,
  History,
  Bell,
  HelpCircle,
  LogOut,
  ChevronRight,
  Crown,
  UserCheck,
  LayoutDashboard,
} from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { PeepingLogo } from "@/components/PeepingLogo";

const ROWS = [
  { icon: FolderOpen, label: "My Content", to: "/creator/photos", creatorOnly: true },
  { icon: Bookmark, label: "Library", to: "/app/library" },
  { icon: History, label: "Purchases & Membership", to: "/app/membership" },
  { icon: Bell, label: "Notifications", to: "/app/settings" },
  { icon: HelpCircle, label: "Terms & Policies", to: "/legal/terms" },
];

export default function Profile() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const isCreator = user?.role === "creator";
  const rows = ROWS.filter((r) => !r.creatorOnly || isCreator);

  const doLogout = async () => {
    await logout();
    navigate("/login", { replace: true });
  };

  return (
    <div data-testid="profile-screen" className="px-5 pt-6">
      <header className="flex items-center justify-between">
        <PeepingLogo className="w-28 items-start" showTagline={false} />
        <button onClick={() => navigate("/app/settings")} data-testid="profile-settings-btn" className="text-white/80">
          <SettingsIcon size={20} />
        </button>
      </header>

      <div className="mt-8 flex flex-col items-center">
        <div className="relative">
          <span className="flex h-24 w-24 items-center justify-center rounded-full bg-[#2a0b4d] text-2xl font-bold ring-2 ring-[#d42be8]/60">{(user?.username || "P").slice(0, 2).toUpperCase()}</span>
          <span className="absolute -bottom-1 left-1/2 -translate-x-1/2 rounded-full bg-[#080808] px-2 py-0.5">
            <Crown size={14} className="text-[#d42be8]" />
          </span>
        </div>
        <h1 className="mt-4 text-xl font-bold">{user?.username || "Your Name"}</h1>
        <p className="text-xs text-[#a7a0af]">{user?.email}</p>
        <span data-testid="profile-role-label" className="mt-2 rounded-full border border-[#d42be8]/40 bg-[#d42be8]/10 px-3 py-1 text-[0.65rem] font-semibold text-[#e6b6f2]">
          {user?.role === "platform_owner_review" ? "Platform Owner · Review account" : user?.role === "external_premium_reviewer" ? "External Reviewer · temporary access" : isCreator ? "Creator · viewing as subscriber" : "Member"}
        </span>
      </div>

      {isCreator && (
        <button onClick={() => navigate("/creator/dashboard")} data-testid="profile-open-studio"
          className="mt-6 flex w-full items-center justify-center gap-2 rounded-2xl border border-[#d42be8]/50 bg-[#d42be8]/10 py-3.5 text-sm font-semibold text-[#f0a8fa]">
          <LayoutDashboard size={16} /> Open Creator Studio
        </button>
      )}

      <button
        onClick={() => navigate("/app/membership")}
        data-testid="profile-manage-membership"
        className="btn-brand mt-6 w-full rounded-2xl py-3.5 text-sm font-semibold text-white"
      >
        Manage Membership
      </button>

      <button onClick={() => navigate("/app/following")} data-testid="profile-following" className="mt-6 flex w-full items-center gap-3 rounded-2xl glass px-4 py-4 text-left">
        <UserCheck size={18} className="text-[#c77dff]" />
        <span className="flex-1 text-sm">Creators I Follow</span>
        <ChevronRight size={16} className="text-[#6f6878]" />
      </button>

      <ul className="mt-3 overflow-hidden rounded-2xl glass">
        {rows.map(({ icon: Icon, label, to }, i) => (
          <li key={label} className={i > 0 ? "border-t border-white/5" : ""}>
            <button onClick={() => navigate(to)} data-testid={`profile-row-${label.split(" ")[0].toLowerCase()}`}
              className="flex w-full items-center gap-3 px-4 py-4 text-left hover:bg-white/[0.03]">
              <Icon size={18} className="text-[#a7a0af]" />
              <span className="flex-1 text-sm">{label}</span>
              <ChevronRight size={16} className="text-[#6f6878]" />
            </button>
          </li>
        ))}
      </ul>

      <button
        onClick={doLogout}
        data-testid="profile-logout"
        className="mt-5 flex w-full items-center justify-center gap-2 rounded-2xl border border-white/10 py-3.5 text-sm font-medium text-red-300 hover:border-red-400/40"
      >
        <LogOut size={16} /> Log Out
      </button>
    </div>
  );
}
